<script setup lang="ts">
import type { Component } from 'vue';
import Button from 'primevue/button';
import PhoneIcon from '@primeicons/vue/phone';
import EnvelopeIcon from '@primeicons/vue/envelope';
import AccountLabelTag from '@apparatix/leads/AccountLabelTag.vue';
import { Stack, Toolbar, Typography } from './core';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';
import TextTruncate from './TextTruncate.vue';

type ModelObject = {
  id: number;
  name: string;
  view: string;
};

type Props = {
  icon: Component
  iconColor: string
  subtitle: string
  title: string
  phone?: string
  email?: string
  hideEmailButton?: boolean
  hidePhoneButton?: boolean
  loading?: boolean;
  modelObject?: ModelObject;
  accountLabel?: { id: number; value: string; color: string } | null;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'on:title-click'): void;
  (e: 'post-activity', payload: { modelObject: ModelObject, typeId: number }): void;
}>();

const onCallClick = () => {
  const modelObject = props.modelObject;
  if (modelObject) {
    emit('post-activity', {
      modelObject,
      typeId: 2,
    });
  }
  setTimeout(() => {
    window.open(`tel:${props.phone}`, '_self');
  }, 50);
};
const onEmailClick = () => {
  const modelObject = props.modelObject;
  if (modelObject) {
    emit('post-activity', {
      modelObject,
      typeId: 1,
    });
  }
  setTimeout(() => {
    window.open(`mailto:${props.email}`, '_self');
  }, 50);
};
</script>
<template>
  <div class="flex items-center justify-between p-3 lg:p-6 rounded-lg bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.10)]">
    <template v-if="!props.loading">
      <Stack :gap="4" class="w-full">
        <Toolbar align="left">
          <component :is="props.icon" :color="props.iconColor" />
          <Typography variant="form-heading">
            {{ props.subtitle }}
          </Typography>
        </Toolbar>
        <div v-if="props.accountLabel" class="w-fit max-w-full">
          <AccountLabelTag :label="props.accountLabel" />
        </div>
        <TextTruncate class="text-base lg:text-lg font-bold leading-10 cursor-pointer hover:underline" :text="props.title" @click="emit('on:title-click')" />
      </Stack>
      <Toolbar align="left">
        <Button
          v-if="!hidePhoneButton"
          icon-only
          variant="outlined"
          :severity="props.phone ? '' : 'secondary'"
          :disabled="!props.phone"
          rounded
          @click="onCallClick"
        >
          <PhoneIcon />
        </Button>
        <Button
          v-if="!hideEmailButton"
          icon-only
          variant="outlined"
          :disabled="!props.email"
          :severity="props.email ? '' : 'secondary'"
          rounded
          @click="onEmailClick"
        >
          <EnvelopeIcon />
        </Button>
      </Toolbar>
    </template>
    <FormSkeleton v-if="props.loading" :rows="3" compact />
  </div>
</template>
